// hooks/useMissionOrderCounters.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import { OfficialType } from '../types';
import {
  getMissionOrderCounters,
  getMissionOrderCounter,
  setMissionOrderCounter,
  getNextMissionOrderNumber,
  getMaxOrderNumber,
  initializeMissionOrderCounters
} from '../services/missionOrderNumberingService';

/**
 * Key roots of the numbering queries. `maxOrderNumber` reads `mission_orders`;
 * the counters advance when an order number is allocated for a new mission
 * order. All four are prefix-invalidated by realtime on `mission_orders` /
 * `mission_order_batches` changes (lib/realtime/globalRealtimeHandlers.ts).
 */
export const MISSION_ORDER_NUMBERING_QUERY_KEY_PREFIXES = [
  'missionOrderCounters',
  'missionOrderCounter',
  'nextMissionOrderNumber',
  'maxOrderNumber',
] as const;

/**
 * Hook to fetch all mission order counters for a league + season
 */
export function useMissionOrderCounters(leagueId: string, season: string) {
  return useQuery({
    queryKey: ['missionOrderCounters', leagueId, season],
    queryFn: () => getMissionOrderCounters(supabase, leagueId, season),
    enabled: !!leagueId && !!season,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch a specific counter for league + official type + season
 */
export function useMissionOrderCounter(leagueId: string, officialType: OfficialType, season: string) {
  return useQuery({
    queryKey: ['missionOrderCounter', leagueId, officialType, season],
    queryFn: () => getMissionOrderCounter(supabase, leagueId, officialType, season),
    enabled: !!leagueId && !!officialType && !!season,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to get next mission order number (preview)
 */
export function useNextMissionOrderNumber(leagueId: string, officialType: OfficialType, season: string) {
  return useQuery({
    queryKey: ['nextMissionOrderNumber', leagueId, officialType, season],
    queryFn: () => getNextMissionOrderNumber(supabase, leagueId, officialType, season),
    enabled: !!leagueId && !!officialType && !!season,
    staleTime: 1 * 60 * 1000, // 1 minute (shorter since this changes frequently)
  });
}

/**
 * Hook to get max order number from existing orders
 */
export function useMaxOrderNumber(leagueId: string, officialType: OfficialType, season: string) {
  return useQuery({
    queryKey: ['maxOrderNumber', leagueId, officialType, season],
    queryFn: () => getMaxOrderNumber(supabase, leagueId, officialType, season),
    enabled: !!leagueId && !!officialType && !!season,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to update a mission order counter
 */
export function useSetMissionOrderCounter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      leagueId: string;
      officialType: OfficialType;
      newValue: number;
      season: string;
    }) => {
      return setMissionOrderCounter(
        supabase,
        params.leagueId,
        params.officialType,
        params.newValue,
        params.season
      );
    },
    onSuccess: (_, variables) => {
      // Invalidate all related queries
      queryClient.invalidateQueries({
        queryKey: ['missionOrderCounters', variables.leagueId]
      });
      queryClient.invalidateQueries({
        queryKey: ['missionOrderCounter', variables.leagueId, variables.officialType]
      });
      queryClient.invalidateQueries({
        queryKey: ['nextMissionOrderNumber', variables.leagueId, variables.officialType]
      });
    },
    onError: (error) => {
      console.error('Error setting mission order counter:', error);
    }
  });
}

/**
 * Hook to initialize counters for a new league
 */
export function useInitializeMissionOrderCounters() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      leagueId: string;
      refereeStartNumber?: number;
      delegateStartNumber?: number;
    }) => {
      return initializeMissionOrderCounters(
        supabase,
        params.leagueId,
        params.refereeStartNumber,
        params.delegateStartNumber
      );
    },
    onSuccess: (_, variables) => {
      // Invalidate all related queries for this league
      queryClient.invalidateQueries({
        queryKey: ['missionOrderCounters', variables.leagueId]
      });
      queryClient.invalidateQueries({
        queryKey: ['missionOrderCounter', variables.leagueId]
      });
      queryClient.invalidateQueries({
        queryKey: ['nextMissionOrderNumber', variables.leagueId]
      });
    },
    onError: (error) => {
      console.error('Error initializing mission order counters:', error);
    }
  });
}
